const temporary = id => String(id || "").startsWith("offline-report-");
const list = value => Array.isArray(value) ? value : [];

// Server IDs are canonical. Retain the local client identity even when a later
// server response omits it, so older cached placeholders can still be matched.
function reportNormalizer(candidates, resolvedIds) {
  candidates = candidates.filter(report => report?._id);
  const aliases = new Map(Object.entries(resolvedIds));
  const byId = new Map();
  for (const report of candidates) {
    if (!temporary(report._id)) {
      byId.set(report._id, { ...byId.get(report._id), ...report });
      if (report._clientId) {
        aliases.set(report._clientId, report._id);
        aliases.set(`offline-report-${report._clientId}`, report._id);
      }
    }
  }
  const resolve = id => {
    const seen = new Set();
    while (aliases.has(id) && !seen.has(id)) { seen.add(id); id = aliases.get(id); }
    return id;
  };
  // Recover client identity from the old placeholder if only the ID map remains.
  for (const report of candidates) {
    const id = resolve(report._id);
    if (id !== report._id && report._clientId && byId.has(id)) {
      byId.set(id, { _clientId: report._clientId, ...byId.get(id) });
    }
  }
  return report => {
    const id = resolve(report._id);
    const canonical = byId.get(id);
    const normalized = temporary(report._id) && id !== report._id
      ? { ...report, ...canonical, _id: id }
      : { ...canonical, ...report, _id: id };
    if (!temporary(id) && normalized._offlineQueued) normalized._offlineQueued = false;
    if (typeof normalized.siteId === "string") normalized.siteId = resolve(normalized.siteId);
    return normalized;
  };
}

export function reconcileReportList(incoming, known = [], resolvedIds = {}) {
  const normalize = reportNormalizer([...list(known), ...list(incoming)], resolvedIds);
  const merged = new Map();
  for (const report of list(incoming)) {
    if (!report?._id) continue;
    const normalized = normalize(report);
    merged.set(normalized._id, { ...merged.get(normalized._id), ...normalized });
  }
  return [...merged.values()];
}

export function mergeReportDataRecords(previous, incoming, resolvedIds = {}, knownReports = []) {
  const records = [...list(previous), ...list(incoming)].filter(record => record?.report?._id);
  const normalize = reportNormalizer([...knownReports, ...records.map(record => record.report)], resolvedIds);
  const merged = new Map();
  const canonicalSources = new Set();
  for (const record of records) {
    const report = normalize(record.report);
    const id = report._id;
    const isCanonical = record.report._id === id;
    const existing = merged.get(id);
    // A stale placeholder must never overwrite data already stored under its
    // server ID. Among canonical snapshots the latest incoming data wins.
    const next = !isCanonical && canonicalSources.has(id)
      ? { ...record, ...existing, report: { ...report, ...existing.report } }
      : { ...existing, ...record, report: { ...existing?.report, ...report } };
    if (id !== record.report._id || report.siteId !== record.report.siteId) {
      for (const field of ["works", "observations", "files", "keyIssueMatrixItems"]) {
        if (Array.isArray(next[field])) next[field] = next[field].map(item => ({ ...item, reportId: id,
          ...(typeof report.siteId === "string" ? { siteId: report.siteId } : {}) }));
      }
      if (Array.isArray(next.pendingKeyIssueChanges)) next.pendingKeyIssueChanges = next.pendingKeyIssueChanges.map(change => ({
        ...change, item: { ...change.item, reportId: id,
          ...(typeof report.siteId === "string" ? { siteId: report.siteId } : {}) },
      }));
      if (next.siteOverview?.reportId) next.siteOverview = { ...next.siteOverview, reportId: id };
    }
    merged.set(id, next);
    if (isCanonical) canonicalSources.add(id);
  }
  return [...merged.values()];
}
